/**
 * Domain vocabulary shared by the Worker and the SPA.
 *
 * Every one of these arrays is the single source of truth: the Drizzle
 * schema builds its CHECK constraints from them, the Zod schemas validate
 * against them, and the UI renders labels from them.
 *
 * The values were derived from `HM - Active Inventory.xlsx` rather than from
 * the project brief — see docs/inventory-findings.md. Where the two
 * disagreed, the real inventory won.
 */

/**
 * Slugified from the MEDIA TYPE column. There are no posters and no junior
 * posters anywhere in the inventory, despite the brief listing both.
 */
/**
 * What kind of work a job is.
 *
 * Not every visit hangs vinyl. A repair — a torn face, a failed light, storm
 * damage — has no material to ship and no copy to check, so treating it as an
 * install produces a work order full of blank fields and a checklist that does
 * not apply.
 *
 * Kept to what actually happens today. Adding a type later is one migration,
 * whereas guessing at types nobody uses leaves dead options in a dropdown a
 * crew has to read on a phone.
 */
export const JOB_TYPES = ['install', 'repair'] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  install: 'Vinyl install',
  repair: 'Repair / maintenance',
};

export const MEDIA_TYPES = [
  'bulletin',
  'bulletin_stack',
  'bulletin_digital',
  'wallscape',
  'wallscape_digital',
  'painted_wallscape',
  'windowscape',
  'digital_mesh',
  'projection',
  'trivision',
  'truck_digital',
  'other',
] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];

export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  bulletin: 'Bulletin',
  bulletin_stack: 'Bulletin - Stack',
  bulletin_digital: 'Bulletin - Digital',
  wallscape: 'Wallscape',
  wallscape_digital: 'Wallscape - Digital',
  painted_wallscape: 'Painted Wallscape',
  windowscape: 'Windowscape',
  digital_mesh: 'Digital Mesh',
  projection: 'Projection',
  trivision: 'Trivision',
  truck_digital: 'Truck - Digital',
  other: 'Other',
};

/** Media types that carry no physical vinyl, so they get a different checklist. */
export const DIGITAL_MEDIA_TYPES = [
  'bulletin_digital',
  'wallscape_digital',
  'digital_mesh',
  'projection',
  'truck_digital',
] as const satisfies readonly MediaType[];

export function isDigitalMedia(t: MediaType): boolean {
  return (DIGITAL_MEDIA_TYPES as readonly MediaType[]).includes(t);
}

/** Parses the free-text MEDIA TYPE column into a slug. */
export function parseMediaType(raw: string): MediaType {
  const k = raw.trim().toLowerCase().replace(/[\s_]*-[\s_]*/g, '_').replace(/\s+/g, '_');
  const direct = MEDIA_TYPES.find((m) => m === k);
  if (direct) return direct;
  if (k.includes('trivision')) return 'trivision';
  if (k.includes('mesh')) return 'digital_mesh';
  if (k.includes('projection')) return 'projection';
  if (k.includes('window')) return 'windowscape';
  if (k.includes('painted')) return 'painted_wallscape';
  if (k.includes('wallscape')) return k.includes('digital') ? 'wallscape_digital' : 'wallscape';
  if (k.includes('truck')) return 'truck_digital';
  if (k.includes('stack')) return 'bulletin_stack';
  if (k.includes('bulletin')) return k.includes('digital') ? 'bulletin_digital' : 'bulletin';
  return 'other';
}

/**
 * Compass bearing the face points toward. The inventory spells these out and
 * also contains values that are not a single bearing ("North, South & west",
 * "Varies"), so `multiple` and `varies` are real members and the raw string is
 * kept alongside in `units.face_direction_raw`.
 */
export const FACE_DIRECTIONS = [
  'north',
  'northeast',
  'east',
  'southeast',
  'south',
  'southwest',
  'west',
  'northwest',
  'multiple',
  'varies',
] as const;
export type FaceDirection = (typeof FACE_DIRECTIONS)[number];

export function parseFaceDirection(raw: string): FaceDirection | null {
  const v = raw.trim().toLowerCase();
  if (!v || v === '-') return null;
  if (v === 'varies') return 'varies';
  if (/[,&]|\band\b/.test(v)) return 'multiple';
  const direct = FACE_DIRECTIONS.find((f) => f === v);
  if (direct) return direct;
  // Abbreviated forms, as used in the structure list ("EF", "NEF", "SF").
  const abbr: Record<string, FaceDirection> = {
    n: 'north', ne: 'northeast', e: 'east', se: 'southeast',
    s: 'south', sw: 'southwest', w: 'west', nw: 'northwest',
  };
  return abbr[v.replace(/f$/, '')] ?? null;
}

/**
 * Which side approaching traffic reads the face from — the OOH convention
 * printed on the work order. The inventory uses Right/Left/Corner; there is no
 * head-on read, and `corner` was missing from the first cut of this schema.
 */
export const READ_DIRECTIONS = ['right', 'left', 'corner'] as const;
export type ReadDirection = (typeof READ_DIRECTIONS)[number];

/** Long form, exactly as printed on the work order. */
export const READ_DIRECTION_LABELS: Record<ReadDirection, string> = {
  right: 'RIGHT HAND READ',
  left: 'LEFT HAND READ',
  corner: 'CORNER READ',
};

export function parseReadDirection(raw: string): ReadDirection | null {
  const v = raw.trim().toLowerCase();
  if (v.startsWith('r')) return 'right';
  if (v.startsWith('l')) return 'left';
  if (v.startsWith('c')) return 'corner';
  return null;
}

/** The inventory records illumination as Yes / No / "-". */
export const ILLUMINATION_TYPES = ['yes', 'no', 'unknown'] as const;
export type Illumination = (typeof ILLUMINATION_TYPES)[number];

export function parseIllumination(raw: string): Illumination {
  const v = raw.trim().toLowerCase();
  if (v === 'yes' || v === 'y' || v === 'true' || v === 'illuminated') return 'yes';
  if (v === 'no' || v === 'n' || v === 'false') return 'no';
  return 'unknown';
}

export const PHOTO_CATEGORIES = ['before', 'during', 'after', 'damage', 'other'] as const;
export type PhotoCategory = (typeof PHOTO_CATEGORIES)[number];

export const INVOICE_APPROVAL_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type InvoiceApprovalStatus = (typeof INVOICE_APPROVAL_STATUSES)[number];

export const EMAIL_DIRECTIONS = ['inbound', 'outbound'] as const;
export type EmailDirection = (typeof EMAIL_DIRECTIONS)[number];

/**
 * `queued` is written before the provider is called; everything after it is
 * reconciled from the Resend webhook. Nothing sends without a row first.
 */
export const EMAIL_STATUSES = [
  'queued',
  'sent',
  'delivered',
  'bounced',
  'complained',
  'failed',
  'received',
] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export const ACTOR_TYPES = ['staff', 'installer', 'system', 'inbound_email'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const ATTACHMENT_KINDS = ['ad_copy', 'proof', 'spreadsheet', 'work_order_pdf', 'other'] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

/** Parses "14' x 48'" into feet. Tolerates the non-breaking spaces in the sheet. */
export function parseSize(raw: string): { heightFt: number; widthFt: number } | null {
  const cleaned = raw.replace(/ /g, ' ').trim();
  const m = /(\d+(?:\.\d+)?)\s*'?\s*[xX×]\s*(\d+(?:\.\d+)?)/.exec(cleaned);
  if (!m) return null;
  const h = Number(m[1]);
  const w = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(w)) return null;
  return { heightFt: h, widthFt: w };
}

/** Renders dimensions the way the work order prints them: `14' x 48'`. */
export function formatDimensions(heightFt: number | null, widthFt: number | null): string | null {
  if (heightFt === null || widthFt === null) return null;
  const n = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
  return `${n(heightFt)}' x ${n(widthFt)}'`;
}
